import {saveAs} from "file-saver";
import {http} from "./api";
import {http as filesHttp} from "./api/restApiClient";
import Alert from "react-s-alert";
import {FileError} from "./api/errorHelpers";

const SUPPORTED_FILE_TYPES: string[] = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
];

export type FileReference = Readonly<{
  /**
   * UUID.
   */
  id: string;
  /**
   * S3 public link.
   */
  url: string;
}>;

type FileUploadUrl = Readonly<{
  /**
   * S3 pre-signed URL.
   */
  uploadUrl: string;
}>;

export const FileService = {
  get: (fileId: string) => http.get(`/files/${fileId}`),
  /**
   * Gets file reference with pre-signed URL from API Gateway.
   * Used for getting pre-signed URLs for file downloads.
   *
   * @param fileId - File UUID
   * @returns FileReference with pre-signed URL
   */
  getFileReference: (fileId: string) =>
    filesHttp.get<FileReference>(`/v3/files/${fileId}`),
  upload: async (file: File, bucket: string, givenName: string) => {
    const maxFileSize = parseInt(
      import.meta.env.VITE_MAX_FILE_UPLOAD_SIZE || "5",
      10,
    );
    const maxFileBytes = maxFileSize * 1024 * 1024;

    if (file.size > maxFileBytes) {
      throw new FileError(
        `File is too large. Must be less than ${maxFileSize} megabytes.`,
      );
    }

    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      throw new FileError(`File type ${file.type} is not supported.`);
    }

    const getUploadUrlResponse = await filesHttp.post<FileUploadUrl>(
      "/v3/files",
      {
        type: bucket,
        name: file.name,
      },
    );

    if (!getUploadUrlResponse.ok || !getUploadUrlResponse.data?.uploadUrl) {
      throw new FileError("Failed getting upload url.");
    }

    const uploadResponse = await fetch(getUploadUrlResponse.data.uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new FileError("Failed uploading file to S3.");
    }

    return filesHttp.post<FileReference>("/v3/files/add", {
      type: bucket,
      name: file.name,
      contentType: file.type,
      givenName,
    });
  },
  download: async (fileId: string) => {
    const response = await filesHttp.get<FileReference>(`/v3/files/${fileId}`);

    if (!response.ok) {
      throw new FileError("Failed getting file from API.");
    }

    const fileResponse = await fetch(response.data.url, {
      method: "GET",
      headers: {
        "Content-Type": "application/vnd.ms-excel",
      },
    });

    if (!fileResponse.ok) {
      throw new FileError("Failed downloading file from S3.");
    }

    return fileResponse.blob();
  },
};

export const saveExcel = async (fileId: string, fileName: string) => {
  try {
    const fileContent = await FileService.download(fileId);
    saveAs(fileContent, fileName);
  } catch {
    Alert.error("Failed to download Excel.");
  }
};
